// 运行时配置工具
// 优先使用 window.APP_CONFIG，如果不存在则回退到环境变量

declare global {
  interface Window {
    APP_CONFIG?: {
      API_BASE_URL?: string;
      TITLE?: string;
      DESCRIPTION?: string;
      SITE_URL?: string;
      THEME_COLOR?: string;
      CRISP_WEBSITE_ID?: string;
      TELEGRAM_LOGIN_BOT_USERNAME?: string;
      CLIENT_DOWNLOADS?: {
        WINDOWS?: string;
        MACOS?: string;
        ANDROID?: string;
      };
      CLIENT_VERSION?: string;
    };
  }
}

/**
 * 获取API基础URL
 * 优先级：window.APP_CONFIG.API_BASE_URL > process.env.REACT_APP_API_BASE_URL > 默认值
 */
export const getApiBaseUrl = (): string => {
  return (
    window.APP_CONFIG?.API_BASE_URL ||
    process.env.REACT_APP_API_BASE_URL ||
    'https://api.yijianlian.club/api/v1'
  );
};

/**
 * 获取应用标题
 * 优先级：window.APP_CONFIG.TITLE > process.env.REACT_APP_TITLE > 默认值
 */
export const getAppTitle = (): string => {
  return (
    window.APP_CONFIG?.TITLE ||
    process.env.REACT_APP_TITLE ||
    '一键连'
  );
};

/**
 * 获取站点 URL（用于 canonical、sitemap、结构化数据）
 */
export const getSiteUrl = (): string => {
  const configured = window.APP_CONFIG?.SITE_URL || process.env.REACT_APP_SITE_URL;
  if (configured) return configured.replace(/\/$/, '');
  // 未配置时自动使用当前访问域名（适合多域名部署）
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return '';
};

/**
 * 获取应用描述
 */
export const getAppDescription = (): string => {
  return (
    window.APP_CONFIG?.DESCRIPTION ||
    '一键连是专业的跨境网络服务提供商，支持跨境电商、TikTok运营、海外AI工具（ChatGPT/Claude）及国际协作，提供 NodeUnion 官方客户端与全球业务节点。'
  );
};

/**
 * 获取主题色
 */
export const getThemeColor = (): string => {
  return (
    window.APP_CONFIG?.THEME_COLOR ||
    '#000000'
  );
};

/**
 * 获取Crisp的网站ID
 */
export const getCrispWebsiteId = (): string => {
  return (
    window.APP_CONFIG?.CRISP_WEBSITE_ID ||
    process.env.REACT_APP_CRISP_WEBSITE_ID ||
    ''
  );
};

/**
 * 获取 Telegram Login Widget 机器人用户名
 */
export const getTelegramLoginBotUsername = (): string => {
  return (
    window.APP_CONFIG?.TELEGRAM_LOGIN_BOT_USERNAME ||
    process.env.REACT_APP_TELEGRAM_LOGIN_BOT_USERNAME ||
    ''
  );
};

export interface ClientDownloadInfo {
  url: string;
  label: string;
  description: string;
  appName?: string;
}

export interface ClientDownloads {
  windows: ClientDownloadInfo;
  macos: ClientDownloadInfo;
  android: ClientDownloadInfo;
  ios: ClientDownloadInfo;
}

const DEFAULT_CLIENT_DOWNLOADS = {
  WINDOWS: 'https://github.com/maiyulao/NodeUnion/releases/download/0.2.01/NodeUnion-0.2.01-windows-amd64-setup.exe',
  MACOS: 'https://github.com/maiyulao/NodeUnion/releases/download/0.2.01/NodeUnion-0.2.01-macos-arm64.dmg',
  ANDROID: 'https://github.com/maiyulao/NodeUnion/releases/download/0.2.01/NodeUnion-0.2.01-android-arm64-v8a.apk',
};

const DEFAULT_CLIENT_VERSION = '0.2.01';

/**
 * 官方客户端下载链接（可在 public/config.js 中覆盖）
 */
export const getClientDownloads = (): ClientDownloads => {
  const cfg = window.APP_CONFIG?.CLIENT_DOWNLOADS;
  const version = window.APP_CONFIG?.CLIENT_VERSION || DEFAULT_CLIENT_VERSION;

  const windowsUrl =
    cfg?.WINDOWS ||
    process.env.REACT_APP_CLIENT_DOWNLOAD_WINDOWS ||
    DEFAULT_CLIENT_DOWNLOADS.WINDOWS;
  const macosUrl =
    cfg?.MACOS ||
    process.env.REACT_APP_CLIENT_DOWNLOAD_MACOS ||
    DEFAULT_CLIENT_DOWNLOADS.MACOS;
  const androidUrl =
    cfg?.ANDROID ||
    process.env.REACT_APP_CLIENT_DOWNLOAD_ANDROID ||
    DEFAULT_CLIENT_DOWNLOADS.ANDROID;

  return {
    windows: {
      url: windowsUrl,
      label: 'Windows',
      description: `NodeUnion v${version} · Windows 10/11 64 位`,
    },
    macos: {
      url: macosUrl,
      label: 'macOS',
      description: `NodeUnion v${version} · Apple Silicon (ARM64)`,
      appName: 'NodeUnion.app',
    },
    android: {
      url: androidUrl,
      label: 'Android',
      description: `NodeUnion v${version} · ARM64`,
    },
    ios: {
      url: '/help',
      label: 'iOS',
      description: '登录用户中心查看 iOS 配置教程',
    },
  };
};

/**
 * 获取所有配置
 */
export const getConfig = () => {
  return {
    apiBaseUrl: getApiBaseUrl(),
    title: getAppTitle(),
    description: getAppDescription(),
    siteUrl: getSiteUrl(),
    themeColor: getThemeColor(),
    crispWebsiteId: getCrispWebsiteId(),
    telegramLoginBotUsername: getTelegramLoginBotUsername(),
    clientDownloads: getClientDownloads(),
  };
};
